import { useEffect } from "react";

/**
 * Draws the champagne seam on every `.section-seam` as it enters the
 * viewport. One IntersectionObserver for the whole page rather than a
 * ScrollTrigger per section — this runs on every route, so it must stay cheap.
 *
 * Mounted once in PageShell. Re-scans on route change because the new page's
 * sections don't exist yet when the previous observer was created.
 */
export function useSectionSeams(routeKey) {
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;

    const nodes = Array.from(document.querySelectorAll(".section-seam"));
    if (!nodes.length) return undefined;

    const reduce =
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      nodes.forEach((n) => n.classList.add("is-in"));
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { rootMargin: "0px 0px -12% 0px", threshold: 0 }
    );

    // Sections already on screen at mount should draw immediately.
    nodes.forEach((n) => {
      const r = n.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) n.classList.add("is-in");
      else io.observe(n);
    });

    return () => io.disconnect();
  }, [routeKey]);
}
